import { codeSplit, Controller, FetchOptions, FetchPageOptions, AnalyticsStore, PageStore, Result } from "bernie-core";
import { PageData, HttpStatus, Request, Response } from "bernie-http";
import { ExtendedContextStore, FlexBffTemplateResponse, Template } from "typings/flexFramework/FlexDefinitions";
import { TemplateStore } from "stores/TemplateStore";
import { CompositionStore } from "src/stores/CompositionStore";
import { FlexViewModelStore } from "stores/flexViewModel/FlexViewModelStore";
import { BlossomComponentTreeStore } from "stores/BlossomComponentTree";
import { StaticMapStore } from "stores/staticMap/StaticMapStore";
import { BlossomCodeSplitter } from "src/components/flexFramework/CodeSplitter/BlossomCodeSplitter";
import { isVariantEnabled, Experiment } from "components/utility/experiment";
import { FlexModuleModelStore } from "src/stores/FlexModuleModelStore";

interface FlexFetchPageOptions extends FetchPageOptions {
  context: ExtendedContextStore;
}

interface WindowWithState extends Window {
  __STATE__: {
    templateStore: TemplateStore;
  };
}

declare var window: WindowWithState;

/**
 * New controller that currently only serves Hotel-Reviews type pages.
 * Currently work in progress in order to work towards the BEXG Tech Strategy.
 * More info here: More info: https://confluence.expedia.biz/display/~mhaddock/Flex%3A+full+adoption+of+Tech+Strategy
 */
export class FlexController implements Controller {
  public pageId: string; // x-page-id is set in the middleware
  // TODO: Need to find a better way to switch between controllers so we can test other pages using template endpoint
  // Currently only supports Hotel-Review pages based on: https://github.expedia.biz/Flex/ewe-link-service/blob/master/src/main/resources/com/expedia/www/link/config/LinkServiceConfig.json#L2518-L2639
  public path =
    "/*h:id.:name(Hotel-Reviews|Opiniones-Hotel|Comentarios-Hotel|Avis-Voyageurs|Hotelbewertungen|Hotellrecensioner|Evaluations-Hotel|Hotelliarvostelut|Hotelanmeldelser|Recensioni-Hotel|Vurderinger-Hoteller|Hotelbeoordelingen|Avaliacoes-Do-Hotel|Hotel-Pagsusuri|Nhan-Xet|Ulasan-Hotel)";
  public routeName = "flex-FlexPage";
  public bundles = ["flex-FlexPage"];
  public exact = false;
  public component = codeSplit(async () => {
    const routeComponent = import(/* webpackChunkName: "flex-FlexPage" */ "views/FlexPage") as any;
    let template: Template | undefined;

    // Client-side only
    if (typeof window !== "undefined") {
      const serializedState = window.__STATE__;
      template = serializedState && serializedState.templateStore ? serializedState.templateStore.template : undefined;
    }

    await new BlossomCodeSplitter().loadRequiredCodeSplitBundles({ template });

    return routeComponent;
  });

  /**
   * Gets DUAID and MC1 cookies based on deviceId of incoming context request
   * @returns an array of cookies to be propagated to the flex bff
   */
  public getDeviceIdCookiesFromContextRequest = (req: Request) => {
    const deviceId = req && req.context && req.context.deviceId;
    if (!deviceId) {
      return [];
    }

    return [`DUAID=${deviceId}`, `MC1=GUID=${deviceId.replace(/-/g, "")}`];
  };

  /**
   * Gets set-cookie from the response of Bernie's Platform Context
   * @returns an array of cookies to be propagated to the flex bff
   */
  public getCookiesFromContextResponse = (res: Response | undefined) => {
    if (!res || !res.headers || !res.headers["set-cookie"]) {
      return [];
    }

    // In case set-cookies is only one entry and is a string
    const setCookies = res.headers["set-cookie"];
    if (typeof setCookies === "string") {
      return [setCookies];
    }

    return setCookies;
  };

  public fetch(options: FetchOptions): Promise<Result> {
    const { stores } = options;
    const flexViewModelStore = stores && stores.get<FlexViewModelStore>("flexViewModel");
    const pageStore = stores && stores.get<PageStore>("page");
    const contextStore = stores && stores.get<ExtendedContextStore>("context");
    const templateStore = stores && stores.get<TemplateStore>("templateStore");
    const compositionStore = stores && stores.get<CompositionStore>("compositionStore");
    const flexModuleModelStore = stores && stores.get<FlexModuleModelStore>("flexModuleModelStore");
    const blossomComponentTreeStore = stores && stores.get<BlossomComponentTreeStore>("blossomComponentTreeStore");
    const analyticsStore = stores && stores.get<AnalyticsStore>("analytics");
    const mapStore = stores && stores.get<StaticMapStore>("staticMap");

    // We get it from the middleware which hijacks the contextStore
    const flexBffResponse = contextStore.viewModelResponse as FlexBffTemplateResponse;
    const footer: string = contextStore.footer;
    delete contextStore.footer;
    delete contextStore.viewModelResponse;

    flexViewModelStore.setupFlexViewModelStore(flexBffResponse, pageStore, contextStore, analyticsStore, footer);
    templateStore.setup(flexBffResponse, contextStore, flexModuleModelStore, blossomComponentTreeStore);
    compositionStore.setup(flexBffResponse, contextStore, flexModuleModelStore, blossomComponentTreeStore, mapStore);

    /* istanbul ignore next */
    if (isVariantEnabled(contextStore, Experiment.Flex_Enable_Graphql_Modules)) {
      compositionStore.isInFlexEnableGraphqlModulesTest = true; // you are only really in the test if you hit FlexController

      return Promise.resolve({
        status: HttpStatus.OK,
        next: () => this.fetchDeferredData(options),
      });
    }

    return Promise.resolve({
      status: HttpStatus.OK,
    });
  }

  /**
   * A promise to fetch Flex Composition data. Will be executed in parallel with GraphQL queries by bernie-server
   * @param options
   * @returns Promise<any>
   */
  public fetchDeferredData(options: FetchOptions): Promise<any> {
    const templateStore = options.stores.get<TemplateStore>("templateStore");
    const compositionStore = options.stores.get<CompositionStore>("compositionStore");
    const contextStore = options.stores && options.stores.get<ExtendedContextStore>("context");
    const flexModuleModelStore = options.stores.get<FlexModuleModelStore>("flexModuleModelStore");

    const cookies = this.getDeviceIdCookiesFromContextRequest(options.request).concat(
      this.getCookiesFromContextResponse(options.response)
    );

    const requestHeaders = options.request && options.request.headers;
    const headers = {
      ...requestHeaders,
      cookie: cookies,
    };

    return compositionStore.fetchCompositionFromTemplate(
      templateStore.template,
      contextStore,
      flexModuleModelStore,
      headers
    );
  }

  public fetchPageData(options: FlexFetchPageOptions): Promise<PageData> {
    const bffResponse = options.context.viewModelResponse as FlexBffTemplateResponse;
    const additionalScripts = new BlossomCodeSplitter().getRequiredWebpackChunkNames({
      template: bffResponse.template,
    });
    const seoMetaData = import(/* webpackChunkName: "SeoUtils" */ "utils/seo/SeoUtils").then(({ default: _ }) => {
      return (
        _.buildPageDataWithSeoMetaDataFromFlexBffTemplate(bffResponse) || {
          title: bffResponse.template.title,
        }
      );
    });

    return Promise.resolve(seoMetaData).then((m) => {
      return {
        ...m,
        assetsToIncludeFilter: (assetKey: string) => additionalScripts.includes(assetKey),
      };
    });
  }
}
